import { Edit, Mail } from "lucide-react"
import { alpha } from "@mui/material/styles"
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from "@mui/material"
import type { ChipProps } from "@mui/material"
import { type EmailCampaign } from "@crm-src/lib/store/campaignsSlice"
import { recipientCount } from "@crm-src/lib/recipient-count"

// Maps a campaign status to a MUI Chip palette color (inlined from the
// former getStatusColor Tailwind-class mapping during the MUI 5 migration).
function statusChipColor(status: string): ChipProps["color"] {
  switch (status) {
    case "sent":
      return "success"
    case "failed":
      return "error"
    case "not_sent":
    case "scheduled":
    case "sending":
    case "paused":
      return "warning"
    default:
      return "default"
  }
}

interface CampaignDetailsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  campaign: EmailCampaign | null
  getStatusColor: (status: string) => string
  getStatusLabel: (status: string) => string
  calculateOpenRate: (opened: number, sent: number) => number
  onEdit: (campaign: EmailCampaign) => void
}

export function CampaignDetailsDialog({
  open,
  onOpenChange,
  campaign,
  getStatusColor,
  getStatusLabel,
  calculateOpenRate,
  onEdit,
}: CampaignDetailsDialogProps) {
  return (
    <Dialog open={open} onClose={() => onOpenChange(false)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box
          sx={{
            display: "flex",
            height: 40,
            width: 40,
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "50%",
            bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
            color: "primary.main",
          }}
        >
          <Mail size={20} />
        </Box>
        Campaign Details
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 1 }}>
          {campaign && `View details for "${campaign.subject}"`}
        </DialogContentText>
        {campaign && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, py: 2 }}>
            {/* Campaign Info */}
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3 }}>
              <Box>
                <Typography component="label" sx={{ fontSize: 12, fontWeight: 500, color: "text.secondary", textTransform: "uppercase" }}>Subject</Typography>
                <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: "text.primary", mt: 0.5 }}>{campaign.subject}</Typography>
              </Box>
              <Box>
                <Typography component="label" sx={{ fontSize: 12, fontWeight: 500, color: "text.secondary", textTransform: "uppercase" }}>Status</Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip size="small" color={statusChipColor(campaign.status) ?? "default"} label={getStatusLabel(campaign.status)} />
                </Box>
              </Box>
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3 }}>
              <Box>
                <Typography component="label" sx={{ fontSize: 12, fontWeight: 500, color: "text.secondary", textTransform: "uppercase" }}>Created By</Typography>
                <Typography sx={{ fontSize: "1rem", color: "text.primary", mt: 0.5 }}>{campaign.created_by?.name || 'Unknown'}</Typography>
              </Box>
              <Box>
                <Typography component="label" sx={{ fontSize: 12, fontWeight: 500, color: "text.secondary", textTransform: "uppercase" }}>Recipients</Typography>
                <Typography sx={{ fontSize: "1rem", color: "text.primary", mt: 0.5 }}>
                  {recipientCount(campaign) > 0
                    ? `${recipientCount(campaign)} ${campaign.recipients_count && campaign.recipients_count > 0 ? 'Recipients' : 'Selected'}`
                    : campaign.recipient_type === 'customers' ? 'All Customers'
                    : campaign.recipient_type === 'prospects' ? 'All Prospects'
                    : campaign.recipient_type === 'all' ? 'All Contacts'
                    : 'None'
                  }
                </Typography>
              </Box>
            </Box>

            <Box sx={{ borderTop: 1, borderColor: "divider", pt: 3 }}>
              <Typography component="h3" sx={{ fontSize: 14, fontWeight: 600, color: "text.primary", mb: 2 }}>Performance Metrics</Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 2 }}>
                <Box sx={{ textAlign: "center", p: 2, bgcolor: (t) => alpha(t.palette.primary.main, 0.12), borderRadius: 1 }}>
                  <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, color: "primary.main" }}>{campaign.sent_count}</Typography>
                  <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}>Sent</Typography>
                </Box>
                <Box sx={{ textAlign: "center", p: 2, bgcolor: (t) => alpha(t.palette.success.main, 0.12), borderRadius: 1 }}>
                  <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, color: "success.main" }}>{campaign.opened_count}</Typography>
                  <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}>Opens</Typography>
                </Box>
                <Box sx={{ textAlign: "center", p: 2, bgcolor: (t) => alpha(t.palette.warning.main, 0.12), borderRadius: 1 }}>
                  <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, color: "warning.main" }}>{campaign.clicked_count}</Typography>
                  <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}>Clicks</Typography>
                </Box>
                <Box sx={{ textAlign: "center", p: 2, bgcolor: (t) => alpha(t.palette.secondary.main, 0.12), borderRadius: 1 }}>
                  <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, color: "secondary.main" }}>
                    {calculateOpenRate(campaign.opened_count, campaign.sent_count)}%
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}>Open Rate</Typography>
                </Box>
              </Box>
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3, pt: 2, borderTop: 1, borderColor: "divider" }}>
              <Box>
                <Typography component="label" sx={{ fontSize: 12, fontWeight: 500, color: "text.secondary", textTransform: "uppercase" }}>Created</Typography>
                <Typography sx={{ fontSize: 14, color: "text.primary", mt: 0.5 }}>
                  {new Date(campaign.created_at).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </Typography>
              </Box>
              <Box>
                <Typography component="label" sx={{ fontSize: 12, fontWeight: 500, color: "text.secondary", textTransform: "uppercase" }}>Last Updated</Typography>
                <Typography sx={{ fontSize: 14, color: "text.primary", mt: 0.5 }}>
                  {new Date(campaign.updated_at).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ gap: 1 }}>
        <Button variant="outlined" color="inherit" onClick={() => onOpenChange(false)}>
          Close
        </Button>
        {campaign && campaign.status !== 'sent' && campaign.status !== 'sending' && (
          <Button
            variant="contained"
            onClick={() => {
              onOpenChange(false)
              onEdit(campaign)
            }}
            startIcon={<Edit size={16} />}
          >
            Edit Campaign
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
